import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ROW_HEIGHT, QUARTER_HEIGHT } from '../constants';
import { toTwo, getCurrentWeek, timeToMinutes } from '../utils';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * Cuántos días hacia adelante (desde el inicio de la semana actual) se permite
 * navegar en la agenda. 49 días = 7 semanas de offset, es decir 8 semanas
 * visibles contando la semana en curso.
 */
const MAX_FUTURE_OFFSET_DAYS = 49;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseCalendarNavigationOptions {
  /** Passed to the keyboard guard: prevents key handling while data loads */
  loading: boolean;
  /** Passed to the keyboard guard: prevents key handling while refreshing */
  refreshing: boolean;
  /** Passed to the keyboard guard: prevents key handling until template is ready */
  templateReady: boolean;
  /** Passed to the keyboard guard: prevents key handling while the user is dragging */
  dragging: unknown;
  /** The current work-schedule template, used for viewport auto-adjustment */
  template: Array<{ active: boolean; startTime: string; endTime: string }>;
  /** Optional callback fired after checking whether schedules exist */
  onTemplateChange?: (hasSchedules: boolean) => void;
  /** Currently selected timezone — resets viewport when changed */
  selectedTimeZone: string;
}

export interface UseCalendarNavigationReturn {
  // ---- State ----------------------------------------------------------------
  startOfWeek: Date;
  setStartOfWeek: React.Dispatch<React.SetStateAction<Date>>;
  now: Date;
  visibleStartIndex: number;
  setVisibleStartIndex: React.Dispatch<React.SetStateAction<number>>;
  visibleCount: number;
  isGridMounted: boolean;
  setIsGridMounted: React.Dispatch<React.SetStateAction<boolean>>;
  // ---- Refs -----------------------------------------------------------------
  visibleStartIndexRef: React.MutableRefObject<number>;
  lastScrollTimeRef: React.MutableRefObject<number>;
  nowTimeoutRef: React.MutableRefObject<number | null>;
  // ---- DOM constants --------------------------------------------------------
  ROW_HEIGHT_PX: number;
  QUARTER_HEIGHT_PX: number;
  // ---- Derived values -------------------------------------------------------
  timeSlots: string[];
  count: number;
  startIdx: number;
  maxStartIdx: number;
  visibleTimeSlots: string[];
  endOfWeek: Date;
  gridKey: string;
  canGoPrev: boolean;
  canGoNext: boolean;
  // ---- Actions --------------------------------------------------------------
  moveWeek: (delta: number) => void;
  goToToday: () => void;
  scheduleNextHourUpdate: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCalendarNavigation(
  options: UseCalendarNavigationOptions,
): UseCalendarNavigationReturn {
  const {
    loading,
    refreshing,
    templateReady,
    dragging,
    template,
    onTemplateChange,
    selectedTimeZone,
  } = options;

  // ---- Core state -----------------------------------------------------------

  const [startOfWeek, setStartOfWeek] = useState<Date>(() => {
    // If the URL contains ?date=YYYY-MM-DD (e.g. navigating from Patients tab),
    // start on the Monday of that week instead of the current week.
    try {
      const params = new URLSearchParams(window.location.search);
      const dateParam = params.get("date");
      if (dateParam) {
        const d = new Date(`${dateParam}T00:00:00`);
        if (!Number.isNaN(d.getTime())) {
          const day = d.getDay();
          const mondayOffset = day === 0 ? -6 : 1 - day;
          const monday = new Date(d);
          monday.setDate(d.getDate() + mondayOffset);
          monday.setHours(0, 0, 0, 0);
          // URL cleanup is deferred to a useEffect so the ?time param can still be read
          return monday;
        }
      }
    } catch { /* ignore */ }
    return getCurrentWeek().start;
  });
  const [now, setNow] = useState<Date>(() => new Date());
  const [visibleStartIndex, setVisibleStartIndex] = useState<number>(() => {
    // If navigated from patients with a ?time=HH:MM param, scroll to that hour
    try {
      const params = new URLSearchParams(window.location.search);
      const timeParam = params.get("time");
      if (timeParam) {
        const h = parseInt(timeParam.split(":")[0], 10);
        if (!Number.isNaN(h)) {
          const halfVisible = Math.floor(10 / 2);
          const targetIndex = Math.max(0, h - halfVisible);
          const maxIdx = Math.max(0, 24 - 10);
          return Math.min(targetIndex, maxIdx);
        }
      }
    } catch { /* ignore */ }
    const currentHour = new Date().getHours();
    const halfVisible = Math.floor(10 / 2);
    const targetIndex = Math.max(0, currentHour - halfVisible);
    const maxIdx = Math.max(0, 24 - 10);
    return Math.min(targetIndex, maxIdx);
  });
  const visibleCount = 10;
  const [isGridMounted, setIsGridMounted] = useState(false);

  // ---- Refs -----------------------------------------------------------------

  const nowTimeoutRef = useRef<number | null>(null);
  const visibleStartIndexRef = useRef(visibleStartIndex);
  visibleStartIndexRef.current = visibleStartIndex;
  const lastScrollTimeRef = useRef<number>(0);
  const hasAdjustedViewportRef = useRef<boolean>(false);

  // ---- DOM constants (stable after mount) ----------------------------------

  const ROW_HEIGHT_PX = useMemo(() => {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return ROW_HEIGHT * fontSize;
  }, []);

  const QUARTER_HEIGHT_PX = useMemo(() => {
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    return QUARTER_HEIGHT * fontSize;
  }, []);

  // ---- Time slots -----------------------------------------------------------

  const timeSlots: string[] = useMemo(
    () => Array.from({ length: 24 }, (_, h) => `${toTwo(h)}:00`),
    [],
  );

  const count = useMemo(() => {
    const maxCount = Math.min(10, timeSlots.length || 0);
    return Math.max(4, Math.min(Math.min(visibleCount, 10), maxCount || 4));
  }, [timeSlots.length]);

  const maxStartIdx = Math.max(0, timeSlots.length - count);
  const startIdx = Math.max(0, Math.min(visibleStartIndex || 0, maxStartIdx));
  const visibleTimeSlots = timeSlots.slice(startIdx, startIdx + count);

  // ---- Derived navigation values -------------------------------------------

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);

  const gridKey = startOfWeek.toISOString();

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const canGoPrev = true;

  const currentWeek = getCurrentWeek();
  const maxFutureStart = new Date(currentWeek.start);
  maxFutureStart.setDate(currentWeek.start.getDate() + MAX_FUTURE_OFFSET_DAYS);
  const canGoNext = startOfWeek < maxFutureStart;

  // ---- Actions --------------------------------------------------------------

  const scheduleNextHourUpdate = useCallback(() => {
    const nowDate = new Date();
    const msToNextHour =
      ((60 - nowDate.getMinutes()) * 60 - nowDate.getSeconds()) * 1000;
    nowTimeoutRef.current = window.setTimeout(() => {
      setNow(new Date());
      scheduleNextHourUpdate();
    }, Math.max(1000, msToNextHour));
  }, []);

  const moveWeek = useCallback(
    (delta: number): void => {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + delta * 7);
      const current = getCurrentWeek();
      const maxFuture = new Date(current.start);
      maxFuture.setDate(current.start.getDate() + MAX_FUTURE_OFFSET_DAYS);
      if (d > maxFuture) setStartOfWeek(maxFuture);
      else setStartOfWeek(d);
    },
    [startOfWeek],
  );

  const goToToday = useCallback((): void => {
    const { start } = getCurrentWeek();
    setStartOfWeek(start);
  }, []);

  // ---- Effects --------------------------------------------------------------

  // Clean up URL params (?date, ?time) after they have been consumed by state initializers
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  // Keyboard handler: Arrow keys navigate time rows and weeks
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (loading || refreshing || !templateReady || dragging) return;

      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const step = e.key === 'ArrowUp' ? -1 : 1;
        const maxIdx = Math.max(0, 24 - visibleCount);
        setVisibleStartIndex(prev => Math.max(0, Math.min(prev + step, maxIdx)));
      }

      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();
        const delta = e.key === 'ArrowLeft' ? -1 : 1;
        moveWeek(delta);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [loading, refreshing, templateReady, dragging, moveWeek]);

  // Auto-scroll to current hour when today is in the visible week
  useEffect(() => {
    const currentNow = new Date();
    const todayMidnight = new Date(
      currentNow.getFullYear(),
      currentNow.getMonth(),
      currentNow.getDate(),
    );
    const weekStartMidnight = new Date(
      startOfWeek.getFullYear(),
      startOfWeek.getMonth(),
      startOfWeek.getDate(),
    );
    const weekEndMidnight = new Date(weekStartMidnight);
    weekEndMidnight.setDate(weekStartMidnight.getDate() + 6);

    if (todayMidnight >= weekStartMidnight && todayMidnight <= weekEndMidnight) {
      const currentHour = currentNow.getHours();
      const halfVisible = Math.floor(visibleCount / 2);
      const targetIndex = Math.max(0, currentHour - halfVisible);
      const maxIdx = Math.max(0, 24 - visibleCount);
      setVisibleStartIndex(Math.min(targetIndex, maxIdx));
      hasAdjustedViewportRef.current = true;
    }
  }, [startOfWeek]);

  // Reset viewport and caches when timezone changes
  useEffect(() => {
    const currentNow = new Date();
    const currentHour = currentNow.getHours();
    const halfVisible = Math.floor(visibleCount / 2);
    const targetIndex = Math.max(0, currentHour - halfVisible);
    const maxIdx = Math.max(0, 24 - visibleCount);
    setVisibleStartIndex(Math.min(targetIndex, maxIdx));
    hasAdjustedViewportRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeZone]);

  // Viewport auto-adjustment when template schedules load for the first time
  useEffect(() => {
    if (!onTemplateChange) return;

    const hasSchedules = template.length > 0 && template.some(t => t.active);
    onTemplateChange(hasSchedules);

    if (hasSchedules && template.length > 0 && !hasAdjustedViewportRef.current) {
      const activeSchedules = template.filter(t => t.active);
      if (activeSchedules.length > 0) {
        const startTimes = activeSchedules.map(t => timeToMinutes(t.startTime));
        const endTimes = activeSchedules.map(t => timeToMinutes(t.endTime));
        const minStart = Math.min(...startTimes);
        const maxEnd = Math.max(...endTimes);

        const minHourIndex = Math.max(0, Math.floor(minStart / 60) - 1);
        const maxHourIndex = Math.min(23, Math.ceil(maxEnd / 60) + 1);

        setVisibleStartIndex(current => {
          if (current > minHourIndex || current + visibleCount < maxHourIndex) {
            hasAdjustedViewportRef.current = true;
            return Math.max(0, minHourIndex);
          }
          return current;
        });
      }
    }
  }, [template, onTemplateChange]);

  // ---------------------------------------------------------------------------

  return {
    // State
    startOfWeek,
    setStartOfWeek,
    now,
    visibleStartIndex,
    setVisibleStartIndex,
    visibleCount,
    isGridMounted,
    setIsGridMounted,
    // Refs
    visibleStartIndexRef,
    lastScrollTimeRef,
    nowTimeoutRef,
    // DOM constants
    ROW_HEIGHT_PX,
    QUARTER_HEIGHT_PX,
    // Derived values
    timeSlots,
    count,
    startIdx,
    maxStartIdx,
    visibleTimeSlots,
    endOfWeek,
    gridKey,
    canGoPrev,
    canGoNext,
    // Actions
    moveWeek,
    goToToday,
    scheduleNextHourUpdate,
  };
}
